import React, {useState, useEffect, useContext} from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import { FiPlus, FiFileText, FiCheckCircle, FiClock, FiCalendar, FiRepeat, FiX } from 'react-icons/fi';
import { CustomDatePicker } from '../commons/datepicker';
import '../../styles/DogReportsTab.css';
import AssignReportModal from './AssignReportModal';
import {TrainerContext} from "../../context/TrainerContext";
import {useToast} from "../commons/toast/ToastContext";

const DogReportsTab = ( {dogReports = { assignments: [], submissions: [] }, dogId, canFill = false} ) => {
    const { t, i18n } = useTranslation(['dogs', 'common']);
    const { trainer } = useContext(TrainerContext);
    const toast = useToast();
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [finishDate, setFinishDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeSubmissionId, setActiveSubmissionId] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [localDogReports, setLocalDogReports] = useState(dogReports);
    const [fillModalOpen, setFillModalOpen] = useState(false);
    const [selectedFillSubmission, setSelectedFillSubmission] = useState(null);
    const [formAnswers, setFormAnswers] = useState({});

    // Fetch templates for assignment modal
    const fetchTemplates = async () => {
        try {
            const token = localStorage.getItem('jwt');
            const response = await axios.get(`/api/v1/reports/templates/school/${trainer.schoolId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTemplates(response.data);
        } catch (err) {
            console.error('Error fetching templates:', err);
            toast.error(t('dogs:reports.fetchTemplatesError'), t('common:error'));
        }
    };

    // Fetch reports data
    const fetchReports = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('jwt');
            const response = await axios.get(`/api/v1/reports/dog/${dogId}/reports`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLocalDogReports(response.data);
        } catch (err) {
            console.error('Error fetching reports:', err);
            setError(t('dogs:reports.fetchReportsError'));
            toast.error(t('dogs:reports.fetchReportsError'), t('common:error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    // Update local state when prop changes
    useEffect(() => {
        setLocalDogReports(dogReports);
    }, [dogReports]);

    // Set today as default finish date when modal opens
    useEffect(() => {
        if (isFinishModalOpen) {
            const today = new Date();
            setFinishDate(today.toISOString().split('T')[0]);
        }
    }, [isFinishModalOpen]);

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return t('dogs:reports.notSpecified');
        const date = new Date(dateString);
        return date.toLocaleDateString(i18n.language === 'pl' ? 'pl-PL' : 'en-US', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Get recurrence label
    const getRecurrenceLabel = (recurrence) => {
        const recurrenceMap = {
            'ONCE': t('dogs:reports.recurrence.once'),
            'DAILY': t('dogs:reports.recurrence.daily'),
            'EVERY_2_DAYS': t('dogs:reports.recurrence.every2Days'),
            'EVERY_3_DAYS': t('dogs:reports.recurrence.every3Days'),
            'EVERY_4_DAYS': t('dogs:reports.recurrence.every4Days'),
            'EVERY_5_DAYS': t('dogs:reports.recurrence.every5Days'),
            'EVERY_6_DAYS': t('dogs:reports.recurrence.every6Days'),
            'WEEKLY': t('dogs:reports.recurrence.weekly')
        };
        return recurrenceMap[recurrence] || recurrence;
    };

    // Handle report assignment creation
    const handleReportAssigned = async () => {
        // Instead of reloading the page, fetch the updated data
        await fetchReports();
        toast.success(t('dogs:reports.assignedSuccess'), t('common:success'));
    };

    // Handle submission item click with proper event handling
    const handleSubmissionClick = (e, submissionId) => {
        // Toggle the active submission
        setActiveSubmissionId(activeSubmissionId === submissionId ? null : submissionId);
    };

    // Handle opening finish assignment modal
    const handleFinishAssignment = (assignment) => {
        setSelectedAssignment(assignment);
        setIsFinishModalOpen(true);
    };

    // Handle finishing assignment
    const handleSubmitFinishAssignment = async () => {
        if (!selectedAssignment || !finishDate) {
            toast.warning(t('dogs:reports.selectEndDate'), t('dogs:reports.missingData'));
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('jwt');

            const requestData = {
                assignmentId: selectedAssignment.id,
                date: finishDate
            };

            await axios.post('/api/v1/reports/assignment/finish', requestData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Update the local state to reflect the changes
            setLocalDogReports(prevState => ({
                ...prevState,
                assignments: prevState.assignments.map(assignment =>
                    assignment.id === selectedAssignment.id
                        ? { ...assignment, endDate: finishDate }
                        : assignment
                )
            }));

            setIsFinishModalOpen(false);
            setSelectedAssignment(null);
            setFinishDate('');
            toast.success(t('dogs:reports.taskFinishedSuccess'), t('common:success'));
        } catch (err) {
            console.error('Error finishing assignment:', err);
            const errorMessage = err.response?.data?.message || t('dogs:reports.finishTaskError');
            toast.error(errorMessage, t('common:error'));
        } finally {
            setLoading(false);
        }
    };

    // --- Fill report handlers (for ownerless dogs) ---

    const handleFillReport = (submission) => {
        setSelectedFillSubmission(submission);
        const initialAnswers = {};
        if (submission.template && submission.template.questions) {
            submission.template.questions.forEach(question => {
                initialAnswers[question.id] = '';
            });
        }
        setFormAnswers(initialAnswers);
        setFillModalOpen(true);
    };

    const handleAnswerChange = (questionId, value) => {
        setFormAnswers(prev => ({
            ...prev,
            [questionId]: value
        }));
    };

    const handleSubmitReport = async () => {
        try {
            setLoading(true);

            const unansweredQuestions = selectedFillSubmission.template.questions.filter(
                question => !formAnswers[question.id] || formAnswers[question.id].trim() === ''
            );

            if (unansweredQuestions.length > 0) {
                toast.warning(t('dogs:reports.answerAllQuestions'));
                setLoading(false);
                return;
            }

            const answers = selectedFillSubmission.template.questions.map(question => ({
                questionId: question.id,
                answerText: formAnswers[question.id] || ''
            }));

            const submissionData = {
                submissionId: selectedFillSubmission.id,
                answers: answers
            };

            const token = localStorage.getItem('jwt');
            await axios.post('/api/v1/reports/submissions/answers', submissionData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setFillModalOpen(false);
            setSelectedFillSubmission(null);
            setFormAnswers({});
            setActiveSubmissionId(null);
            toast.success(t('dogs:reports.reportSavedSuccess'), t('common:success'));

            // Refresh reports data
            await fetchReports();
        } catch (err) {
            console.error('Error submitting report:', err);
            const errorMessage = err.response?.data?.message || t('dogs:reports.reportSaveError');
            toast.error(errorMessage, t('common:error'));
        } finally {
            setLoading(false);
        }
    };

    // Function to generate dates based on recurrence pattern
    function generateDatesBasedOnRecurrence(startDate, endDate, recurrence) {
        const dates = [];
        let currentDate = new Date(startDate);

        // Loop until we reach or exceed the end date
        while (currentDate <= endDate) {
            dates.push(new Date(currentDate)); // Add a copy of the current date

            // Move to the next date based on recurrence pattern
            switch (recurrence) {
                case 'ONCE':
                    // For one-time reports, just add the start date
                    currentDate = new Date(endDate.getTime() + 86400000); // Jump past end date to exit loop
                    break;

                case 'DAILY':
                    currentDate.setDate(currentDate.getDate() + 1);
                    break;

                case 'EVERY_2_DAYS':
                    currentDate.setDate(currentDate.getDate() + 2);
                    break;

                case 'EVERY_3_DAYS':
                    currentDate.setDate(currentDate.getDate() + 3);
                    break;

                case 'EVERY_4_DAYS':
                    currentDate.setDate(currentDate.getDate() + 4);
                    break;

                case 'EVERY_5_DAYS':
                    currentDate.setDate(currentDate.getDate() + 5);
                    break;

                case 'EVERY_6_DAYS':
                    currentDate.setDate(currentDate.getDate() + 6);
                    break;

                case 'WEEKLY':
                    currentDate.setDate(currentDate.getDate() + 7);
                    break;

                default:
                    // Default to daily if recurrence pattern is unknown
                    currentDate.setDate(currentDate.getDate() + 1);
            }
        }

        return dates;
    }

    // Group submissions by assignment with complete date coverage
    const submissionsByAssignment = localDogReports.assignments.map(assignment => {
        // Get all submissions for this assignment
        let assignmentSubmissions = localDogReports.submissions.filter(
            submission => submission.assignmentId === assignment.id
        );

        // Create a Map keyed by date string to handle duplicates
        const submissionsByDate = new Map();

        // First, add all existing submissions to our map
        assignmentSubmissions.forEach(submission => {
            const dueDateStr = submission.dueDate ? new Date(submission.dueDate).toISOString().split('T')[0] : null;

            // If we don't have this date yet, or if this submission is completed and the existing one isn't,
            // use this submission
            if (!submissionsByDate.has(dueDateStr) ||
                (submission.completed && !submissionsByDate.get(dueDateStr).completed)) {
                submissionsByDate.set(dueDateStr, submission);
            }
        });

        // Now generate all expected dates based on recurrence pattern
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to beginning of today

        const startDate = new Date(assignment.startDate);
        startDate.setHours(0, 0, 0, 0); // Set to beginning of start date

        // Determine the end limit for generating submissions
        let endLimit = today;
        if (assignment.endDate) {
            const assignmentEndDate = new Date(assignment.endDate);
            assignmentEndDate.setHours(0, 0, 0, 0);
            // Use the earlier of today or assignment end date
            endLimit = assignmentEndDate < today ? assignmentEndDate : today;
        }

        // Only proceed if the assignment has started
        if (startDate <= endLimit) {
            // Generate all dates from start date to end limit based on recurrence
            const allExpectedDates = generateDatesBasedOnRecurrence(
                startDate,
                endLimit,
                assignment.recurrence
            );

            // Add any missing dates as pending reports
            allExpectedDates.forEach(date => {
                const dateStr = date.toISOString().split('T')[0];

                // If we don't already have a submission for this date, create one
                if (!submissionsByDate.has(dateStr)) {
                    // Create a placeholder submission
                    const pendingSubmission = {
                        id: `pending-${assignment.id}-${date.getTime()}`, // Generate a temporary ID
                        assignmentId: assignment.id,
                        submittedAt: null,
                        dueDate: date.toISOString(),
                        completed: false,
                        template: {
                            id: assignment.reportTemplateId,
                            title: assignment.reportTemplateTitle,
                            questions: [] // This will be populated when the user opens the form
                        },
                        answers: []
                    };

                    submissionsByDate.set(dateStr, pendingSubmission);
                }
            });
        }

        // Convert Map back to array
        const completeSubmissions = Array.from(submissionsByDate.values());

        // Sort submissions by due date
        completeSubmissions.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

        return {
            assignment,
            submissions: completeSubmissions
        };
    });

    if (loading && localDogReports.assignments.length === 0) return <div>{t('dogs:reports.loadingReports')}</div>;
    if (error) return <div className="error">{error}</div>;

    return (
        <div className="dog-reports-tab">
            {submissionsByAssignment.length === 0 ? (
                <div className="dog-reports-header-container">
                    <button
                        className="report-create-btn dog-reports-top-btn"
                        onClick={(e) => {
                            e.preventDefault(); // Prevent default button behavior
                            setIsAssignModalOpen(true);
                        }}
                    >
                        <FiPlus className="reports-btn-icon" />
                        <span>{t('dogs:reports.assignReport')}</span>
                    </button>
                </div>
            ) : (
                /* Only show button when reports exist - no header section */
                <div className="dog-reports-header-container">
                    <button
                        className="report-create-btn"
                        onClick={(e) => {
                            e.preventDefault(); // Prevent default button behavior
                            setIsAssignModalOpen(true);
                        }}
                    >
                        <FiPlus className="reports-btn-icon" />
                        <span>{t('dogs:reports.assignReport')}</span>
                    </button>
                </div>
            )}

            {submissionsByAssignment.length === 0 ? (
                <div className="reports-empty-state">
                    <FiFileText className="reports-empty-icon" />
                    <p>{t('dogs:reports.noAssignedReports')}</p>
                    <p className="reports-empty-subtitle">{canFill ? t('dogs:reports.assignToFill') : t('dogs:reports.assignForOwnerToFill')}</p>
                    <button
                        className="report-create-btn"
                        onClick={(e) => {
                            e.preventDefault(); // Prevent default button behavior
                            setIsAssignModalOpen(true);
                        }}
                    >
                        <FiPlus className="reports-btn-icon" />
                        <span>{t('dogs:reports.assignReport')}</span>
                    </button>
                </div>
            ) : (
                <div className="dog-reports-assignments-list">
                    {submissionsByAssignment.map(({ assignment, submissions }) => {
                        // Check if assignment has ended
                        const assignmentEndDate = assignment.endDate ? new Date(assignment.endDate) : null;
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        let assignmentHasEnded = false;

                        if (assignmentEndDate) {
                            assignmentEndDate.setHours(23, 59, 59, 999);
                            assignmentHasEnded = today > assignmentEndDate;
                        }

                        return (
                            <div key={assignment.id} className="dog-reports-assignment-section">
                                <div className="dog-reports-assignment-header">
                                    <div className="dog-reports-assignment-info">
                                        <h3 className="dog-reports-assignment-template">{assignment.reportTemplateTitle}</h3>
                                        <div className="dog-reports-assignment-meta">
                                            <span className="dog-reports-recurrence-badge">
                                                <FiRepeat className="dog-reports-meta-icon" />
                                                {getRecurrenceLabel(assignment.recurrence)}
                                            </span>
                                            <span className="dog-reports-date-badge">
                                                <FiCalendar className="dog-reports-meta-icon" />
                                                {t('dogs:reports.started')}: {formatDate(assignment.startDate)}
                                            </span>
                                            {assignment.endDate && (
                                                <span className={`dog-reports-date-badge ${assignmentHasEnded ? 'ended' : 'active'}`}>
                                                    <FiCalendar className="dog-reports-meta-icon" />
                                                    {assignmentHasEnded ? t('dogs:reports.ended') + ':' : t('dogs:reports.willEnd') + ':'} {formatDate(assignment.endDate)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {!assignment.endDate && (
                                        <button
                                            className="reports-finish-assignment-btn"
                                            onClick={() => handleFinishAssignment(assignment)}
                                        >
                                            <FiX className="reports-btn-icon" />
                                            {t('dogs:reports.finish')}
                                        </button>
                                    )}
                                </div>

                                <div className="dog-reports-submissions-timeline">
                                    {submissions.length === 0 ? (
                                        <div className="dog-reports-no-submissions">
                                            {t('dogs:reports.noSubmissionsAvailable')}
                                        </div>
                                    ) : (
                                        <>
                                            <h4 className="dog-reports-timeline-title">{t('dogs:reports.submissionHistory')}:</h4>
                                            <div className="dog-reports-submissions-list">
                                                {submissions.map(submission => {
                                                    // Check if the due date is today or in the past
                                                    const dueDate = new Date(submission.dueDate);
                                                    dueDate.setHours(0, 0, 0, 0);

                                                    const today = new Date();
                                                    today.setHours(0, 0, 0, 0);

                                                    // Determine if submission is overdue (due date is today or in the past)
                                                    const isOverdue = dueDate <= today;

                                                    // A submission is pending if it's overdue, not completed, AND the assignment hasn't ended
                                                    const isPending = isOverdue && !submission.completed && !assignmentHasEnded;




                                                    // A submission is expired if the assignment has ended and it's not completed
                                                    const isExpired = assignmentHasEnded && !submission.completed;

                                                    return (
                                                        <div
                                                            key={submission.id}
                                                            className={`dog-reports-submission-item ${
                                                                submission.completed ? 'completed' :
                                                                    isExpired ? 'expired' :
                                                                        isPending ? 'pending' :
                                                                            'upcoming'
                                                            } ${activeSubmissionId === submission.id ? 'active' : ''}`}
                                                            onClick={(e) => handleSubmissionClick(e, submission.id)}
                                                        >
                                                            <div className="dog-reports-submission-header">
                                                                <div className="dog-reports-status-badge-small">
                                                                    {submission.completed ? (
                                                                        <>
                                                                            <FiCheckCircle className="dog-reports-status-icon" />
                                                                            <span>{t('dogs:reports.status.filled')}</span>
                                                                        </>
                                                                    ) : isExpired ? (
                                                                        <>
                                                                            <FiClock className="dog-reports-status-icon" />
                                                                            <span>{t('dogs:reports.status.expired')}</span>
                                                                        </>
                                                                    ) : isPending ? (
                                                                        <>
                                                                            <FiClock className="dog-reports-status-icon" />
                                                                            <span>{t('dogs:reports.status.pending')}</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FiCalendar className="dog-reports-status-icon" />
                                                                            <span>{t('dogs:reports.status.upcoming')}</span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                                <div className="dog-reports-submission-date">
                                                                    <FiCalendar className="dog-reports-date-icon" />
                                                                    {formatDate(submission.dueDate)}
                                                                    {submission.completed && submission.submittedAt && (
                                                                        <span className="dog-reports-submitted-at">
                                                                            ({t('dogs:reports.filledOn')}: {formatDate(submission.submittedAt)})
                                                                        </span>
                                                                    )}
                                                                    {assignmentHasEnded && !submission.completed && (
                                                                        <span className="dog-reports-expired-note">
                                                                            ({t('dogs:reports.taskEnded')}: {formatDate(assignment.endDate)})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Submission details when clicked */}
                                                            {activeSubmissionId === submission.id && (
                                                                <div className="dog-reports-submission-details">
                                                                    <h4>{t('dogs:reports.reportDetails')}:</h4>

                                                                    {submission.completed ? (
                                                                        <div className="dog-reports-answers-list">
                                                                            {submission.answers.map((answer, index) => (
                                                                                <div key={answer.id} className="dog-reports-answer-item">
                                                                                    <div className="dog-reports-answer-question">
                                                                                        <span className="dog-reports-question-number">{index + 1}.</span>
                                                                                        <span className="dog-reports-question-text">{answer.questionText}</span>
                                                                                    </div>
                                                                                    <div className="dog-reports-answer-text">
                                                                                        {answer.answerText || <em>{t('dogs:reports.noAnswer')}</em>}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : isExpired ? (
                                                                        <div className="dog-reports-expired-report">
                                                                            <p>{t('dogs:reports.reportNotFilledTaskEnded', { date: formatDate(assignment.endDate) })}</p>
                                                                        </div>
                                                                    ) : isPending ? (
                                                                        canFill ? (
                                                                            <div className="dog-reports-report-actions">
                                                                                <p>{t('dogs:reports.reportAwaitingFill')}</p>
                                                                                <button
                                                                                    className="dog-reports-fill-report-btn"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleFillReport(submission);
                                                                                    }}
                                                                                >
                                                                                    {t('dogs:reports.fillNow')}
                                                                                </button>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="dog-reports-no-answers">
                                                                                {t('dogs:reports.reportAwaitingOwnerFill')}
                                                                            </div>
                                                                        )
                                                                    ) : (
                                                                        <div className="dog-reports-no-answers">
                                                                            {t('dogs:reports.reportAvailableInFuture')}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Assign Report Modal */}
            {isAssignModalOpen && (
                <AssignReportModal
                    isOpen={isAssignModalOpen}
                    onClose={() => setIsAssignModalOpen(false)}
                    dogId={dogId}
                    templates={templates}
                    onAssigned={handleReportAssigned}
                />
            )}

            {/* Finish Assignment Modal */}
            {isFinishModalOpen && selectedAssignment && (
                <div className="reports-modal-overlay">
                    <div className="reports-modal dog-reports-finish-assignment-modal">
                        <div className="reports-modal-header">
                            <h2 className="reports-modal-title">{t('dogs:reports.finishTask')}: {selectedAssignment.reportTemplateTitle}</h2>
                            <button
                                className="reports-close-btn"
                                onClick={() => {
                                    setIsFinishModalOpen(false);
                                    setSelectedAssignment(null);
                                    setFinishDate('');
                                }}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="reports-modal-content">
                            <p className="dog-reports-finish-description">
                                {t('dogs:reports.finishDescription')}
                            </p>

                            <div className="reports-input-group">
                                <label htmlFor="finishDate">{t('dogs:reports.endDate')}</label>
                                <CustomDatePicker
                                    id="finishDate"
                                    name="finishDate"
                                    value={finishDate}
                                    onChange={(e) => setFinishDate(e.target.value)}
                                    max={new Date().toISOString().split('T')[0]}
                                    required
                                />
                            </div>

                            <div className="dog-reports-assignment-summary">
                                <h4>{t('dogs:reports.taskSummary')}:</h4>
                                <div className="dog-reports-summary-item">
                                    <span className="dog-reports-summary-label">{t('dogs:reports.template')}:</span>
                                    <span className="dog-reports-summary-value">{selectedAssignment.reportTemplateTitle}</span>
                                </div>
                                <div className="dog-reports-summary-item">
                                    <span className="dog-reports-summary-label">{t('dogs:reports.startDate')}:</span>
                                    <span className="dog-reports-summary-value">{formatDate(selectedAssignment.startDate)}</span>
                                </div>
                                <div className="dog-reports-summary-item">
                                    <span className="dog-reports-summary-label">{t('dogs:reports.repetition')}:</span>
                                    <span className="dog-reports-summary-value">{getRecurrenceLabel(selectedAssignment.recurrence)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="reports-modal-footer">
                            <button
                                className="reports-cancel-btn"
                                onClick={() => {
                                    setIsFinishModalOpen(false);
                                    setSelectedAssignment(null);
                                    setFinishDate('');
                                }}
                                disabled={loading}
                            >
                                {t('common:cancel')}
                            </button>
                            <button
                                className="dog-reports-finish-btn reports-save-btn"
                                onClick={handleSubmitFinishAssignment}
                                disabled={loading || !finishDate}
                            >
                                {loading ? t('dogs:reports.finishing') : t('dogs:reports.finishTaskBtn')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Fill Report Modal (for ownerless dogs) */}
            {fillModalOpen && selectedFillSubmission && (
                <div className="reports-modal-overlay">
                    <div className="reports-modal dog-reports-fill-modal">
                        <div className="reports-modal-header">
                            <h2 className="reports-modal-title">{t('dogs:reports.fillReport')}: {selectedFillSubmission.template.title}</h2>
                            <button
                                className="reports-close-btn"
                                onClick={() => {
                                    setFillModalOpen(false);
                                    setSelectedFillSubmission(null);
                                    setFormAnswers({});
                                }}
                            >
                                &times;
                            </button>
                        </div>

                        <div className="reports-modal-content">
                            <div className="dog-reports-fill-meta">
                                <span>{t('dogs:reports.reportDate')}: {formatDate(selectedFillSubmission.dueDate)}</span>
                            </div>

                            {selectedFillSubmission.template.questions && selectedFillSubmission.template.questions.length > 0 ? (
                                <div className="dog-reports-fill-questions-list">
                                    {selectedFillSubmission.template.questions
                                        .sort((a, b) => a.orderIndex - b.orderIndex)
                                        .map((question, index) => (
                                            <div key={question.id} className="dog-reports-fill-question-item">
                                                <div className="dog-reports-fill-question-header">
                                                    <div className="dog-reports-fill-question-number">{index + 1}.</div>
                                                    <div className="dog-reports-fill-question-text">{question.questionText}</div>
                                                </div>
                                                <div className="dog-reports-fill-answer-input">
                                                    <textarea
                                                        placeholder={t('dogs:reports.typeAnswer')}
                                                        value={formAnswers[question.id] || ''}
                                                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                                        rows="3"
                                                        required
                                                    ></textarea>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            ) : (
                                <div className="dog-reports-fill-empty-questions">
                                    {t('dogs:reports.noQuestions')}
                                </div>
                            )}
                        </div>

                        <div className="reports-modal-footer">
                            <button
                                className="reports-cancel-btn"
                                onClick={() => {
                                    setFillModalOpen(false);
                                    setSelectedFillSubmission(null);
                                    setFormAnswers({});
                                }}
                                disabled={loading}
                            >
                                {t('common:cancel')}
                            </button>
                            {selectedFillSubmission.template.questions && selectedFillSubmission.template.questions.length > 0 && (
                                <button
                                    className="reports-save-btn"
                                    onClick={handleSubmitReport}
                                    disabled={loading}
                                >
                                    {loading ? t('common:saving') : t('dogs:reports.saveReport')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DogReportsTab;
